import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './BiddingStrategy.css';  // Add this import
const API_URL = process.env.NODE_ENV === 'production' 
          ? ''
          : 'http://localhost:8000';

const BiddingStrategy = () => {
  // State hooks
  const [isLoading, setIsLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [categoryAnalysis, setCategoryAnalysis] = useState([]);
  const [productAnalysis, setProductAnalysis] = useState([]);
  const [filter, setFilter] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState('');
  const [isDatabaseConnected, setIsDatabaseConnected] = useState(false);
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sourceFile, setSourceFile] = useState('');
  const [createdStartDate, setCreatedStartDate] = useState('');
  const [createdEndDate, setCreatedEndDate] = useState('');
  const [sourceFiles, setSourceFiles] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Bid suggestion state
  const [product, setProduct] = useState('');
  const [bestOffer, setBestOffer] = useState('');
  const [suggestion, setSuggestion] = useState(null);

  // Store product search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [storeProductAnalysis, setStoreProductAnalysis] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // New states for Bid Helper by Store Product
  const [storeProductSearchQuery, setStoreProductSearchQuery] = useState('');
  const [storeProductSearchResults, setStoreProductSearchResults] = useState([]);
  const [selectedStoreProduct, setSelectedStoreProduct] = useState(null);
  const [storeProductBestOffer, setStoreProductBestOffer] = useState('');
  const [storeBidSuggestion, setStoreBidSuggestion] = useState(null);
  const [isStoreProductSearching, setIsStoreProductSearching] = useState(false);
  const [isStoreProductLoading, setIsStoreProductLoading] = useState(false);
  const [bidHelperType, setBidHelperType] = useState('description'); // 'description' or 'storeProduct'

  // Check database connection and load data on component mount
  useEffect(() => {
    const checkDbConnection = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/check-db-connection`);
        setIsDatabaseConnected(response.data.connected);
        if (!response.data.connected) {
          setError('Database connection failed. Please check server configuration.');
        } else {
          loadAllData();
        }
      } catch (err) {
        setIsDatabaseConnected(false);
        setError('Database connection failed. Please check server configuration.');
      }
    };

    checkDbConnection();
  }, []);

  // Load all data for the dashboard
  const loadAllData = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Build query parameters for filters
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (sourceFile) params.append('source_file', sourceFile);
      if (createdStartDate) params.append('created_start_date', createdStartDate);
      if (createdEndDate) params.append('created_end_date', createdEndDate);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      // Load available source files for filter dropdown
      if (sourceFiles.length === 0) {
        try {
          const sourceFilesResponse = await axios.get(`${API_URL}/api/source-files`);
          if (sourceFilesResponse.data.success) {
            setSourceFiles(sourceFilesResponse.data.files || []);
          }
        } catch (error) {
          console.error('Error loading source files:', error);
          // Non-critical, so continue with main data loading
        }
      }
      
      // Load overall statistics
      const statsPromise = axios.get(`${API_URL}/api/bidding-statistics`);
      
      // Load category analysis
      const categoryPromise = axios.get(`${API_URL}/api/category-analysis`);
      
      // Load product analysis
      const productPromise = axios.get(`${API_URL}/api/product-analysis`);
      
      // Wait for all requests to complete
      const [statsResponse, categoryResponse, productResponse] = await Promise.all([
        statsPromise, categoryPromise, productPromise
      ]);
      
      console.log('Product analysis response:', productResponse.data);
      
      setStatistics(statsResponse.data.data);
      setCategoryAnalysis(categoryResponse.data.categories);
      setProductAnalysis(productResponse.data.products || []);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Error loading data: ' + (error.response?.data?.detail || error.message));
      setIsLoading(false);
    }
  };

  // Get bid suggestion for a specific product
  const getBidSuggestion = async () => {
    if (!product || !bestOffer) {
      toast.error('Please enter both product description and best offer');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api/optimal-overbid`, {
        params: {
          product_description: product,
          best_offer: parseFloat(bestOffer)
        }
      });
      
      setSuggestion(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error getting bid suggestion:', error);
      setError('Error getting bid suggestion: ' + (error.response?.data?.detail || error.message));
      setIsLoading(false);
      toast.error('Failed to get bid suggestion');
    }
  };

  // Search for store products
  const searchStoreProducts = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Please enter a search query');
      return;
    }
    
    try {
      setSearchLoading(true);
      setError('');
      
      const response = await axios.get(`${API_URL}/api/search-store-products`, {
        params: { query: searchQuery }
      });
      
      setSearchResults(response.data.products || []);
      setSearchLoading(false);
    } catch (error) {
      console.error('Error searching store products:', error);
      setError('Error searching products: ' + (error.response?.data?.detail || error.message));
      setSearchLoading(false);
      toast.error('Failed to search products');
    }
  };

  // Toggle product selection
  const toggleProductSelection = (product) => {
    const isSelected = selectedProducts.some(p => p.product_id === product.product_id);
    
    if (isSelected) {
      setSelectedProducts(selectedProducts.filter(p => p.product_id !== product.product_id));
    } else {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  // Analyze selected store products
  const analyzeSelectedProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.warning('Please select at least one product to analyze');
      return;
    }
    
    try {
      setAnalysisLoading(true);
      setError('');
      
      const productIds = selectedProducts.map(p => p.product_id).join(',');
      const response = await axios.get(`${API_URL}/api/store-product-analysis`, {
        params: { product_ids: productIds }
      });
      
      setStoreProductAnalysis(response.data);
      setAnalysisLoading(false);
    } catch (error) {
      console.error('Error analyzing store products:', error);
      setError('Error analyzing products: ' + (error.response?.data?.detail || error.message));
      setAnalysisLoading(false);
      toast.error('Failed to analyze products');
    }
  };

  // Search for store products by name/model
  const searchStoreProductsForBid = async () => {
    if (!storeProductSearchQuery) return;
    
    setIsStoreProductSearching(true);
    try {
      const response = await axios.get(`${API_URL}/api/search-store-products`, {
        params: { query: storeProductSearchQuery, limit: 20 }
      });
      
      if (response.data.success) {
        setStoreProductSearchResults(response.data.products);
      } else {
        toast.error('Failed to search products: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error searching products:', error);
      toast.error('Error searching products: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsStoreProductSearching(false);
    }
  };

  // Select a store product for bid suggestion
  const selectStoreProduct = (product) => {
    setSelectedStoreProduct(product);
    setStoreProductSearchResults([]);
    setStoreProductSearchQuery('');
  };

  // Get bid suggestion based on store product ID
  const getStoreBidSuggestion = async () => {
    if (!selectedStoreProduct || !storeProductBestOffer) {
      toast.warning('Please select a product and enter the best offer amount');
      return;
    }
    
    setIsStoreProductLoading(true);
    setStoreBidSuggestion(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/optimal-overbid-by-product`, {
        params: {
          product_id: selectedStoreProduct.product_id,
          best_offer: parseFloat(storeProductBestOffer)
        }
      });
      
      if (response.data.success) {
        setStoreBidSuggestion(response.data);
      } else {
        toast.error('Failed to get bid suggestion: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error getting bid suggestion:', error);
      toast.error('Error getting bid suggestion: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsStoreProductLoading(false);
    }
  };

  // Filter the product analysis data
  const filteredProducts = productAnalysis.filter(
    product => product.product_description?.toLowerCase().includes(filter.toLowerCase())
  );

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return '£' + parseFloat(value).toFixed(2);
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value === null || value === undefined) return '—';
    return parseFloat(value).toFixed(1) + '%';
  };

  // Render success rate with color
  const renderSuccessRate = (rate) => {
    if (rate === null || rate === undefined) return '—';
    
    let color;
    if (rate >= 70) color = 'text-success';
    else if (rate >= 50) color = 'text-warning';
    else color = 'text-danger';
    
    return <span className={color}>{parseFloat(rate).toFixed(1)}%</span>;
  };

  return (
    <div className="bidding-strategy-container">
      <ToastContainer 
        position="top-right" 
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastClassName="modern-toast"
      />
      
      <h1 className="page-title">Bidding Strategy Analysis</h1>
      
      {/* Connection status */}
      <div className="connection-status-container">
        <span className="connection-label">Database Connection:</span>
        <span className={`connection-status ${isDatabaseConnected ? 'status-connected' : 'status-disconnected'}`}>
          {isDatabaseConnected ? 'Connected' : 'Disconnected'}
        </span>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="alert-container alert-danger">
          <span className="alert-icon">⚠️</span>
          <span className="alert-message">{error}</span>
        </div>
      )}
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="loading-container">
          <div className="spinner">
            <svg className="spinner-circle" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
            </svg>
          </div>
          <span className="loading-text">Loading data...</span>
        </div>
      )}
      
      {/* Navigation tabs */}
      <div className="tabs-container">
        <nav className="tabs-nav">
          <button 
            className={`tab-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-item ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button 
            className={`tab-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveTab('products')}
          >
            Products
          </button>
          <button 
            className={`tab-item ${activeTab === 'bidHelper' ? 'active' : ''}`}
            onClick={() => setActiveTab('bidHelper')}
          >
            Bid Helper
          </button>
          <button 
            className={`tab-item ${activeTab === 'storeProducts' ? 'active' : ''}`}
            onClick={() => setActiveTab('storeProducts')}
          >
            Store Products
          </button>
        </nav>
      </div>

      {/* Filter UI component */}
      <div className="filter-container">
        <div className="filter-header" onClick={() => setShowFilters(!showFilters)}>
          <h3>Filter Data {showFilters ? '▼' : '▶'}</h3>
        </div>
        
        {showFilters && (
          <div className="filter-card">
            <div className="filter-card-inner">
              <div className="filter-row">
                <div className="filter-group">
                  <label htmlFor="start-date" className="filter-label">Start Date</label>
                  <input
                    id="start-date"
                    type="date"
                    className="filter-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                
                <div className="filter-group">
                  <label htmlFor="end-date" className="filter-label">End Date</label>
                  <input
                    id="end-date"
                    type="date"
                    className="filter-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="filter-row">
                <div className="filter-group">
                  <label htmlFor="created-start-date" className="filter-label">Created Start Date</label>
                  <input
                    id="created-start-date"
                    type="date"
                    className="filter-input"
                    value={createdStartDate}
                    onChange={(e) => setCreatedStartDate(e.target.value)}
                  />
                </div>
                
                <div className="filter-group">
                  <label htmlFor="created-end-date" className="filter-label">Created End Date</label>
                  <input
                    id="created-end-date"
                    type="date"
                    className="filter-input"
                    value={createdEndDate}
                    onChange={(e) => setCreatedEndDate(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="filter-row">
                <div className="filter-group wide">
                  <label htmlFor="source-file" className="filter-label">Source File</label>
                  <select
                    id="source-file"
                    className="filter-select"
                    value={sourceFile}
                    onChange={(e) => setSourceFile(e.target.value)}
                  >
                    <option value="">All Source Files</option>
                    {sourceFiles.map(file => (
                      <option key={file} value={file}>{file}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="filter-actions">
                <button 
                  className="primary-button"
                  onClick={loadAllData}
                >
                  Apply Filters
                </button>
                <button 
                  className="secondary-button"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    setSourceFile('');
                    setCreatedStartDate('');
                    setCreatedEndDate('');
                    loadAllData();
                  }}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Overview tab */}
      {activeTab === 'overview' && !isLoading && statistics && (
        <div className="overview-container">
          <h2 className="section-title">Bidding Performance Overview</h2>
          
          <div className="stats-grid">
            {/* Overall success rate */}
            <div className="stat-card">
              <div className="stat-card-inner">
                <h5 className="stat-title">Success Rate</h5>
                <div className="stat-value-container">
                  <span className={`stat-value ${
                    statistics.success_rate >= 70 ? 'success-text' : 
                    statistics.success_rate >= 50 ? 'warning-text' : 'danger-text'
                  }`}>
                    {formatPercent(statistics.success_rate)}
                  </span>
                  <span className="stat-subtitle">of bids won</span>
                </div>
                <p className="stat-description">
                  {statistics.success_count} successful out of {statistics.total_records} total bids
                </p>
              </div>
            </div>
            
            {/* Average overbid */}
            <div className="stat-card">
              <div className="stat-card-inner">
                <h5 className="stat-title">Avg. Winning Overbid</h5>
                <div className="stat-value-container">
                  <span className="stat-value primary-text">
                    {formatCurrency(statistics.avg_overbid_amount)}
                  </span>
                  <span className="stat-subtitle">({formatPercent(statistics.avg_overbid_percent)})</span>
                </div>
                <p className="stat-description">
                  Min successful overbid: {formatCurrency(statistics.min_overbid)}
                </p>
              </div>
            </div>
            
            {/* Recent performance */}
            <div className="stat-card">
              <div className="stat-card-inner">
                <h5 className="stat-title">Recent Performance (30 days)</h5>
                <div className="stat-value-container">
                  <span className={`stat-value ${
                    statistics.recent_success_rate >= 70 ? 'success-text' : 
                    statistics.recent_success_rate >= 50 ? 'warning-text' : 'danger-text'
                  }`}>
                    {formatPercent(statistics.recent_success_rate)}
                  </span>
                  <span className="stat-subtitle">success rate</span>
                </div>
                <p className="stat-description">
                  {statistics.recent_success} successful out of {statistics.recent_total} recent bids
                </p>
              </div>
            </div>
          </div>
          
          <div className="insights-card">
            <div className="insights-card-inner">
              <h3 className="insights-title">Key Insights</h3>
              <ul className="insights-list">
                <li className="insights-item">
                  <span className="insights-icon success-icon">✓</span>
                  Average winning bid is {formatCurrency(statistics.avg_overbid_amount)} ({formatPercent(statistics.avg_overbid_percent)}) above the best offer
                </li>
                <li className="insights-item">
                  <span className="insights-icon success-icon">✓</span>
                  Minimum successful overbid amount was {formatCurrency(statistics.min_overbid)}
                </li>
                <li className="insights-item">
                  <span className="insights-icon success-icon">✓</span>
                  Overall success rate is {formatPercent(statistics.success_rate)}
                </li>
                {statistics.recent_success_rate > statistics.success_rate && (
                  <li className="insights-item">
                    <span className="insights-icon success-icon">✓</span>
                    Recent success rate has improved by {formatPercent(statistics.recent_success_rate - statistics.success_rate)} compared to overall average
                  </li>
                )}
                {statistics.recent_success_rate < statistics.success_rate && (
                  <li className="insights-item">
                    <span className="insights-icon warning-icon">!</span>
                    Recent success rate has declined by {formatPercent(statistics.success_rate - statistics.recent_success_rate)} compared to overall average
                  </li>
                )}
              </ul>
            </div>
          </div>
          
          <div className="recommendations-card">
            <div className="recommendations-card-inner">
              <h3 className="recommendations-title">Recommendations</h3>
              <ul className="recommendations-list">
                <li className="recommendations-item">
                  <span className="recommendations-icon info-icon">i</span>
                  Consider bidding at least {formatCurrency(statistics.min_overbid)} above the best offer
                </li>
                <li className="recommendations-item">
                  <span className="recommendations-icon info-icon">i</span>
                  For higher success rates, bid {formatCurrency(statistics.avg_overbid_amount)} above best offer
                </li>
                <li className="recommendations-item">
                  <span className="recommendations-icon info-icon">i</span>
                  Check the category-specific recommendations for more targeted bidding
                </li>
                <li className="recommendations-item">
                  <span className="recommendations-icon info-icon">i</span>
                  Use the Bid Helper tool for product-specific recommendations
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Categories tab */}
      {activeTab === 'categories' && !isLoading && (
        <div className="categories-container">
          <h2 className="section-title">Category Analysis</h2>
          
          <div className="table-container">
            <table className="styled-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="text-center">Total Bids</th>
                  <th className="text-center">Success Rate</th>
                  <th className="text-center">Avg Overbid</th>
                  <th className="text-center">Min Overbid</th>
                  <th className="text-center">Recommended</th>
                </tr>
              </thead>
              <tbody>
                {categoryAnalysis.map((category, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                    <td className="category-name">{category.category}</td>
                    <td className="text-center">{category.total_bids || 0}</td>
                    <td className="text-center">{renderSuccessRate(category.success_rate)}</td>
                    <td className="text-center">{formatCurrency(category.avg_overbid_amount)}</td>
                    <td className="text-center">{formatCurrency(category.min_overbid_amount)}</td>
                    <td className="text-center recommended-value">{formatCurrency(category.balanced_overbid)}</td>
                  </tr>
                ))}
                {categoryAnalysis.length === 0 && (
                  <tr>
                    <td colSpan="6" className="empty-message">
                      No category data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="insights-card mt-6">
            <div className="insights-card-inner">
              <h3 className="insights-title">Category Insights</h3>
              <p className="insights-subtitle">
                Different product categories have different optimal bidding strategies.
                Here are some insights based on your historical data:
              </p>
              <div className="category-insights-container">
                {categoryAnalysis.length > 0 ? (
                  <>
                    <div className="top-categories">
                      {categoryAnalysis
                        .filter(c => c.total_bids >= 5)
                        .sort((a, b) => b.success_rate - a.success_rate)
                        .slice(0, 3)
                        .map((category, index) => (
                          <div key={index} className="category-insight-card success">
                            <h4 className="category-name">{category.category}</h4>
                            <p className="category-stats">
                              {formatPercent(category.success_rate)} success rate with average overbid of {formatCurrency(category.avg_overbid_amount)}.
                            </p>
                            <p className="category-recommendation">
                              For best results, bid <span className="highlight">{formatCurrency(category.balanced_overbid)}</span> above best offer.
                            </p>
                          </div>
                        ))}
                    </div>
                    
                    <div className="challenging-categories">
                      {categoryAnalysis
                        .filter(c => c.total_bids >= 5 && c.success_rate < 50)
                        .sort((a, b) => a.success_rate - b.success_rate)
                        .slice(0, 2)
                        .map((category, index) => (
                          <div key={`low-${index}`} className="category-insight-card warning">
                            <h4 className="category-name">{category.category}</h4>
                            <p className="category-stats">
                              Low success rate ({formatPercent(category.success_rate)}).
                            </p>
                            <p className="category-recommendation">
                              Consider higher bids (at least <span className="highlight">{formatCurrency(category.aggressive_overbid)}</span>) or being more selective.
                            </p>
                          </div>
                        ))}
                    </div>
                  </>
                ) : (
                  <p className="empty-insights-message">Not enough data to provide category insights</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Products tab */}
      {activeTab === 'products' && !isLoading && (
        <div className="products-container">
          <h2 className="section-title">Product Analysis</h2>
          <p className="section-description">
            View and analyze the performance of individual products across auctions to inform your bidding strategy.
          </p>
          
          <div className="search-card">
            <div className="search-card-inner">
              <div className="search-input-group">
                <div className="search-input-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Filter by product description..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                  <button className="search-button" onClick={() => setFilter('')} disabled={!filter}>
                    {filter ? 'Clear' : 'Search'}
                  </button>
                </div>
              </div>
              
              {productAnalysis && productAnalysis.length > 0 ? (
                <>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Product Description</th>
                          <th>Category</th>
                          <th className="text-center">Total Bids</th>
                          <th className="text-center">Success Rate</th>
                          <th className="text-center">Avg Winning Overbid</th>
                          <th className="text-center">Min Winning Overbid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts && filteredProducts.length > 0 ? (
                          filteredProducts.map((product, index) => (
                            <tr key={index}>
                              <td className="product-description">{product.product_description}</td>
                              <td className="product-category">{product.category}</td>
                              <td className="text-center">{product.bid_count}</td>
                              <td className="text-center">
                                <span className={
                                  product.success_rate >= 70 ? 'success-text' : 
                                  product.success_rate >= 50 ? 'warning-text' : 'danger-text'
                                }>
                                  {product.success_rate !== null && product.success_rate !== undefined ? 
                                    parseFloat(product.success_rate).toFixed(1) + '%' : '—'}
                                </span>
                              </td>
                              <td className="text-center">{formatCurrency(product.avg_successful_overbid)}</td>
                              <td className="text-center">{formatCurrency(product.min_successful_overbid)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="empty-message">
                              {filter ? 'No products found matching your filter' : 'No product data available'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {filteredProducts && filteredProducts.length > 0 && (
                    <div className="results-footer">
                      Showing <span className="results-highlight">{filteredProducts.length}</span> of <span className="results-highlight">{productAnalysis.length}</span> products
                    </div>
                  )}
                </>
              ) : (
                <div className="info-message">
                  <p>No product data is available. This could be because:</p>
                  <ul>
                    <li>There are no products with sufficient bid history (minimum of 3 bids required)</li>
                    <li>The database connection may be experiencing issues</li>
                    <li>The backend API returned an empty dataset</li>
                  </ul>
                  <p>Please check the browser console for debugging information.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Bid Helper tab */}
      {activeTab === 'bidHelper' && (
        <div className="bid-helper-container">
          <h2 className="section-title">Bid Helper</h2>
          <p className="section-description">
            Get optimal bid recommendations based on historical data to maximize your success rate.
          </p>
          
          <div className="bid-helper-type-selector">
            <button 
              className={`tab-button ${bidHelperType === 'description' ? 'active' : ''}`}
              onClick={() => setBidHelperType('description')}
            >
              By Product Description
            </button>
            <button 
              className={`tab-button ${bidHelperType === 'storeProduct' ? 'active' : ''}`}
              onClick={() => setBidHelperType('storeProduct')}
            >
              By Store Product
            </button>
          </div>
          
          {bidHelperType === 'description' && (
            <div className="input-card">
              <div className="input-card-inner">
                <h3 className="card-subtitle">Enter Auction Details</h3>
                
                <div className="form-group">
                  <label htmlFor="product-description" className="form-label">Product Description</label>
                  <input
                    id="product-description"
                    type="text"
                    className="form-input"
                    placeholder="e.g., iPhone 13 Pro 256GB"
                    value={product}
                    onChange={(e) => setProduct(e.target.value)}
                  />
                  <div className="form-hint">Enter the product description as accurately as possible</div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="best-offer" className="form-label">Current Best Offer (£)</label>
                  <input
                    id="best-offer"
                    type="number"
                    className="form-input"
                    placeholder="e.g., 450"
                    value={bestOffer}
                    onChange={(e) => setBestOffer(e.target.value)}
                  />
                  <div className="form-hint">The highest bid in the current auction</div>
                </div>
                
                <div className="form-actions">
                  <button 
                    onClick={getBidSuggestion}
                    disabled={isLoading || !product || !bestOffer}
                    className="primary-button"
                  >
                    {isLoading ? (
                      <span className="button-loading">
                        <svg className="spinner" viewBox="0 0 24 24">
                          <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                        </svg>
                        Calculating...
                      </span>
                    ) : 'Get Bid Suggestion'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {bidHelperType === 'description' && suggestion && suggestion.data && suggestion.data.suggestions && (
            <div className="results-card">
              <div className="results-card-inner">
                <h3 className="results-title">
                  Bid Recommendation for{' '}
                  <span className="results-product-name">{suggestion.data.product_description}</span>
                </h3>
                
                <div className="suggestion-grid">
                  <div className="suggestion-card conservative">
                    <div className="suggestion-card-header">
                      <span className="suggestion-label">Conservative Bid</span>
                      <span className="suggestion-win-rate">~{suggestion.data.suggestions.conservative.win_rate}% Win Rate</span>
                    </div>
                    <div className="suggestion-amount">{formatCurrency(suggestion.data.suggestions.conservative.amount)}</div>
                    <div className="suggestion-overbid">
                      {formatCurrency(suggestion.data.suggestions.conservative.overbid_amount)} over best offer ({formatPercent(suggestion.data.suggestions.conservative.overbid_percent)})
                    </div>
                  </div>
                  
                  <div className="suggestion-card balanced">
                    <div className="suggestion-card-header">
                      <span className="suggestion-label">Balanced Bid</span>
                      <span className="suggestion-win-rate">~{suggestion.data.suggestions.balanced.win_rate}% Win Rate</span>
                    </div>
                    <div className="suggestion-amount">{formatCurrency(suggestion.data.suggestions.balanced.amount)}</div>
                    <div className="suggestion-overbid">
                      {formatCurrency(suggestion.data.suggestions.balanced.overbid_amount)} over best offer ({formatPercent(suggestion.data.suggestions.balanced.overbid_percent)})
                    </div>
                  </div>
                  
                  <div className="suggestion-card aggressive">
                    <div className="suggestion-card-header">
                      <span className="suggestion-label">Aggressive Bid</span>
                      <span className="suggestion-win-rate">~{suggestion.data.suggestions.aggressive.win_rate}% Win Rate</span>
                    </div>
                    <div className="suggestion-amount">{formatCurrency(suggestion.data.suggestions.aggressive.amount)}</div>
                    <div className="suggestion-overbid">
                      {formatCurrency(suggestion.data.suggestions.aggressive.overbid_amount)} over best offer ({formatPercent(suggestion.data.suggestions.aggressive.overbid_percent)})
                    </div>
                  </div>
                </div>
                
                <div className="suggestion-explanation">
                  <h4 className="explanation-title">How we calculated this</h4>
                  <p className="explanation-text">
                    This recommendation is based on historical data for {suggestion.data.category} products.
                    The data source was: {suggestion.data.suggestion_source === 'product' ? 'exact product match' : 
                                        suggestion.data.suggestion_source === 'category' ? 'category analysis' : 'global averages'}.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {bidHelperType === 'storeProduct' && (
            <div className="input-card">
              <div className="input-card-inner">
                <h3 className="card-subtitle">Enter Auction Details</h3>
                
                <div className="form-group">
                  <label htmlFor="storeProductSearch" className="form-label">Search Store Product</label>
                  <div className="search-input-group">
                    <div className="search-input-container">
                      <input
                        id="storeProductSearch"
                        type="text"
                        className="search-input"
                        placeholder="Enter product name or model number..."
                        value={storeProductSearchQuery}
                        onChange={(e) => setStoreProductSearchQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchStoreProductsForBid()}
                        disabled={!!selectedStoreProduct}
                      />
                      <button 
                        className="search-button" 
                        onClick={searchStoreProductsForBid}
                        disabled={isStoreProductSearching || !storeProductSearchQuery || !!selectedStoreProduct}
                      >
                        {isStoreProductSearching ? (
                          <span className="button-loading">
                            <svg className="spinner" viewBox="0 0 24 24">
                              <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                            </svg>
                            Searching...
                          </span>
                        ) : 'Search'}
                      </button>
                    </div>
                  </div>
                </div>
                
                {storeProductSearchResults.length > 0 && (
                  <div className="search-results">
                    <div className="results-header">
                      <h4 className="results-title">Search Results</h4>
                      <span className="results-count">{storeProductSearchResults.length} products found</span>
                    </div>
                    
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Model</th>
                            <th>Price</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeProductSearchResults.map(product => (
                            <tr key={product.product_id}>
                              <td>{product.name}</td>
                              <td>{product.model}</td>
                              <td>{formatCurrency(product.price)}</td>
                              <td>
                                <button 
                                  className="small-button"
                                  onClick={() => selectStoreProduct(product)}
                                >
                                  Select
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {selectedStoreProduct && (
                  <>
                    <div className="selected-product-info">
                      <div className="selected-product-header">
                        <h4 className="selected-product-title">Selected Product</h4>
                        <button 
                          className="small-clear-button"
                          onClick={() => setSelectedStoreProduct(null)}
                        >
                          Clear
                        </button>
                      </div>
                      <div className="selected-product-details">
                        <span className="product-name">{selectedStoreProduct.name}</span>
                        <span className="product-model">Model: {selectedStoreProduct.model}</span>
                        <span className="product-price">Price: {formatCurrency(selectedStoreProduct.price)}</span>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="productBestOffer" className="form-label">Current Best Offer</label>
                      <input
                        id="productBestOffer"
                        type="number"
                        className="form-input"
                        placeholder="Enter the current best offer..."
                        value={storeProductBestOffer}
                        onChange={(e) => setStoreProductBestOffer(e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="form-actions">
                      <button 
                        className="primary-button" 
                        onClick={getStoreBidSuggestion}
                        disabled={isStoreProductLoading || !storeProductBestOffer}
                      >
                        {isStoreProductLoading ? (
                          <span className="button-loading">
                            <svg className="spinner" viewBox="0 0 24 24">
                              <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                            </svg>
                            Calculating...
                          </span>
                        ) : 'Get Bid Suggestion'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          
          {bidHelperType === 'storeProduct' && storeBidSuggestion && storeBidSuggestion.data && storeBidSuggestion.data.suggestions && (
            <div className="results-card">
              <div className="results-card-inner">
                <h3 className="results-title">
                  Bid Recommendation for{' '}
                  <span className="results-product-name">{storeBidSuggestion.data.product_description}</span>
                </h3>
                
                <div className="suggestion-grid">
                  <div className="suggestion-card conservative">
                    <div className="suggestion-card-header">
                      <span className="suggestion-label">Conservative Bid</span>
                      <span className="suggestion-win-rate">~{storeBidSuggestion.data.suggestions.conservative.win_rate}% Win Rate</span>
                    </div>
                    <div className="suggestion-amount">{formatCurrency(storeBidSuggestion.data.suggestions.conservative.amount)}</div>
                    <div className="suggestion-overbid">
                      {formatCurrency(storeBidSuggestion.data.suggestions.conservative.overbid_amount)} over best offer ({formatPercent(storeBidSuggestion.data.suggestions.conservative.overbid_percent)})
                    </div>
                  </div>
                  
                  <div className="suggestion-card balanced">
                    <div className="suggestion-card-header">
                      <span className="suggestion-label">Balanced Bid</span>
                      <span className="suggestion-win-rate">~{storeBidSuggestion.data.suggestions.balanced.win_rate}% Win Rate</span>
                    </div>
                    <div className="suggestion-amount">{formatCurrency(storeBidSuggestion.data.suggestions.balanced.amount)}</div>
                    <div className="suggestion-overbid">
                      {formatCurrency(storeBidSuggestion.data.suggestions.balanced.overbid_amount)} over best offer ({formatPercent(storeBidSuggestion.data.suggestions.balanced.overbid_percent)})
                    </div>
                  </div>
                  
                  <div className="suggestion-card aggressive">
                    <div className="suggestion-card-header">
                      <span className="suggestion-label">Aggressive Bid</span>
                      <span className="suggestion-win-rate">~{storeBidSuggestion.data.suggestions.aggressive.win_rate}% Win Rate</span>
                    </div>
                    <div className="suggestion-amount">{formatCurrency(storeBidSuggestion.data.suggestions.aggressive.amount)}</div>
                    <div className="suggestion-overbid">
                      {formatCurrency(storeBidSuggestion.data.suggestions.aggressive.overbid_amount)} over best offer ({formatPercent(storeBidSuggestion.data.suggestions.aggressive.overbid_percent)})
                    </div>
                  </div>
                </div>
                
                <div className="suggestion-explanation">
                  <h4 className="explanation-title">How we calculated this</h4>
                  <p className="explanation-text">
                    This recommendation is based on historical data for {storeBidSuggestion.data.category} products.
                    The data source was: {storeBidSuggestion.data.suggestion_source === 'product' ? 'exact product match' : 
                                        storeBidSuggestion.data.suggestion_source === 'category' ? 'category analysis' : 'global averages'}.
                    {storeBidSuggestion.data.store_product && 
                      ` The store product "${storeBidSuggestion.data.store_product.name}" was used as reference.`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Store Products tab */}
      {activeTab === 'storeProducts' && (
        <div className="store-products-container">
          <h2 className="section-title">Store Product Analysis</h2>
          <p className="section-description">
            Search for specific store products and analyze their auction performance to get tailored bidding recommendations.
          </p>
          
          <div className="search-card">
            <div className="search-card-inner">
              <h3 className="card-subtitle">Search Products</h3>
              
              <div className="search-input-group">
                <div className="search-input-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by product name or model..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchStoreProducts()}
                  />
                  <button 
                    className="search-button" 
                    onClick={searchStoreProducts}
                    disabled={searchLoading}
                  >
                    {searchLoading ? (
                      <span className="button-loading">
                        <svg className="spinner" viewBox="0 0 24 24">
                          <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                        </svg>
                        Searching...
                      </span>
                    ) : 'Search'}
                  </button>
                </div>
              </div>
              
              {searchResults.length > 0 && (
                <div className="search-results">
                  <div className="results-header">
                    <h4 className="results-title">Search Results</h4>
                    <span className="results-count">{searchResults.length} products found</span>
                  </div>
                  
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th className="checkbox-column"></th>
                          <th>Name</th>
                          <th>Model</th>
                          <th>Price</th>
                          <th>Quantity</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map(product => (
                          <tr 
                            key={product.product_id}
                            className={selectedProducts.some(p => p.product_id === product.product_id) ? 'selected-row' : ''}
                            onClick={() => toggleProductSelection(product)}
                          >
                            <td className="checkbox-column">
                              <input 
                                type="checkbox" 
                                checked={selectedProducts.some(p => p.product_id === product.product_id)}
                                onChange={() => {}} // Handled by row click
                                className="custom-checkbox"
                              />
                            </td>
                            <td>{product.name}</td>
                            <td>{product.model}</td>
                            <td>{formatCurrency(product.price)}</td>
                            <td>{product.quantity}</td>
                            <td>
                              <span className={`status-badge ${product.status === '1' ? 'status-active' : 'status-inactive'}`}>
                                {product.status === '1' ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="actions-bar">
                    <span className="selection-count">
                      {selectedProducts.length} {selectedProducts.length === 1 ? 'product' : 'products'} selected
                    </span>
                    <button
                      className="primary-button"
                      onClick={analyzeSelectedProducts}
                      disabled={selectedProducts.length === 0 || analysisLoading}
                    >
                      {analysisLoading ? (
                        <span className="button-loading">
                          <svg className="spinner" viewBox="0 0 24 24">
                            <circle className="spinner-circle" cx="12" cy="12" r="10" fill="none" strokeWidth="3" />
                          </svg>
                          Analyzing...
                        </span>
                      ) : 'Analyze Selected Products'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {storeProductAnalysis && (
            <div className="analysis-card">
              <div className="analysis-card-inner">
                <div className="analysis-header">
                  <h3 className="card-subtitle">Analysis Results</h3>
                  <span className="product-count">{storeProductAnalysis.product_info.length} products</span>
                </div>
                
                {storeProductAnalysis.product_info.length > 0 && (
                  <div className="product-info-section">
                    <h4 className="info-section-title">Selected Products</h4>
                    <div className="table-container">
                      <table className="data-table compact-table">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Model</th>
                            <th>Price</th>
                            <th>Stock</th>
                          </tr>
                        </thead>
                        <tbody>
                          {storeProductAnalysis.product_info.map(product => (
                            <tr key={product.product_id}>
                              <td>{product.product_id}</td>
                              <td>{product.name}</td>
                              <td>{product.model}</td>
                              <td>{formatCurrency(product.price)}</td>
                              <td>{product.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {storeProductAnalysis.auction_records.length > 0 ? (
                  <>
                    <div className="stats-grid">
                      <div className="stats-card performance-card">
                        <h5 className="stats-card-title">Auction Performance</h5>
                        <div className="stats-content">
                          <div className="stat-item">
                            <span className="stat-label">Total Records:</span>
                            <span className="stat-value">{storeProductAnalysis.analysis.total_records}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Success Rate:</span>
                            <span className={`stat-value ${
                              storeProductAnalysis.analysis.success_rate >= 70 ? 'success-text' : 
                              storeProductAnalysis.analysis.success_rate >= 50 ? 'warning-text' : 'danger-text'
                            }`}>
                              {formatPercent(storeProductAnalysis.analysis.success_rate)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="stats-card strategy-card">
                        <h5 className="stats-card-title">Bidding Strategy</h5>
                        <div className="stats-content">
                          <div className="stat-item">
                            <span className="stat-label">Avg Overbid:</span>
                            <span className="stat-value">{formatCurrency(storeProductAnalysis.analysis.avg_overbid)}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Min Successful Overbid:</span>
                            <span className="stat-value">{formatCurrency(storeProductAnalysis.analysis.min_successful_overbid)}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Suggested Overbid:</span>
                            <span className="stat-value highlight-value">{formatCurrency(storeProductAnalysis.analysis.suggested_overbid)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="auction-records-section">
                      <h4 className="info-section-title">Past Auction Records</h4>
                      <div className="table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Product</th>
                              <th>Best Offer</th>
                              <th>Your Bid</th>
                              <th>Overbid</th>
                              <th>Result</th>
                            </tr>
                          </thead>
                          <tbody>
                            {storeProductAnalysis.auction_records.map((record, index) => (
                              <tr key={index}>
                                <td>{record.auction_date}</td>
                                <td className="product-description">{record.product_description}</td>
                                <td>{formatCurrency(record.best_offer)}</td>
                                <td>{formatCurrency(record.new_offer)}</td>
                                <td>
                                  {formatCurrency(record.overbid_amount)} 
                                  <span className="overbid-percent">
                                    ({formatPercent(record.overbid_percent)})
                                  </span>
                                </td>
                                <td>
                                  <span className={`result-badge ${record.is_invoiced_success ? 'result-success' : 'result-failure'}`}>
                                    {record.is_invoiced_success ? 'Won' : 'Lost'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="info-message">
                    No auction data found for the selected store products. This could mean these products have not been offered in auctions yet.
                  </div>
                )}
                
                <div className="center-actions">
                  <button 
                    className="secondary-button"
                    onClick={() => {
                      setSelectedProducts([]);
                      setStoreProductAnalysis(null);
                      setSearchQuery('');
                      setSearchResults([]);
                    }}
                  >
                    Start New Analysis
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="footer-disclaimer">
        <p>Note: All recommendations are based on historical data. Past performance does not guarantee future results.</p>
      </div>
    </div>
  );
};

export default BiddingStrategy;